import React, { useEffect, useMemo, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import styled from 'styled-components';
import Card from 'v3/components/Card';
import Text from 'v3/components/Text';
import DataTable from 'v3/components/DataTable';
import Modal from 'v3/components/Modal';
import Button from 'v3/components/Button';
import { useParams } from 'react-router-dom';
import { useUser } from './context';
import moment from 'moment';

function formatDate(value) {
	if (!value) return 'Não informado';
	return moment(value).format('DD/MM/YYYY HH:mm:ss');
}

const userAuditTableStyles = {
	borderCollapse: 'collapse',
	'> thead > tr > th': {
		borderBottom: '1px solid #D9E0E8',
		paddingBottom: 14,
	},
	'> tbody > tr > td': {
		borderTop: 'none',
		transition: 'background-color 0.15s ease-in-out',
	},
	'> tbody > tr:not(:last-child) > td': {
		borderBottom: '1px solid #E5E7EB',
	},
	'> tbody > tr:last-child > td': {
		borderBottom: 'none !important',
	},
	'> tbody > tr:hover': {
		background: '#F5F7FA',
	},
	'> tbody > tr:hover > td': {
		background: '#F5F7FA',
	},
};

const DetailsButton = styled(Button)`
	min-width: 96px;
	padding: 8px 14px;

	&:hover {
		opacity: 1 !important;
		background-color: #cfd8e3 !important;
	}
`;

const AuditCard = styled(Card)`
	> div:first-child {
		border-bottom: 2px solid #d9e0e8;
	}
`;

export default function UserAuditCard() {
	const params = useParams();
	const user = useUser();
	const [detailsOpen, setDetailsOpen] = useState(false);
	const [selectedAudit, setSelectedAudit] = useState(null);

	useEffect(() => {
		if (params.id) {
			user.fetchUserAudit();
		}
	}, [params.id, user.audit.page, user.audit.perPage]);

	const columns = useMemo(
		() => [
			{
				name: 'Tipo',
				className: 'align-middle',
				selector: row => row.type?.name || 'Não informado',
			},
			{
				name: 'Evento',
				className: 'align-middle',
				selector: row => row.eventType?.name || 'Não informado',
			},
			{
				name: 'Título',
				className: 'align-middle',
				selector: row => row.title || 'Não informado',
			},
			{
				name: 'Alterado por',
				className: 'align-middle',
				selector: row => row.userChange?.username || 'Sistema',
			},
			{
				name: 'Data',
				className: 'align-middle',
				selector: row => formatDate(row.createdAt),
			},
			{
				name: '',
				title: 'Detalhes',
				className: 'align-middle',
				render: row => (
					<DetailsButton
						variant="secondary"
						size="sm"
						onClick={() => {
							setSelectedAudit(row);
							setDetailsOpen(true);
						}}
					>
						Detalhes
					</DetailsButton>
				),
			},
		],
		[]
	);

	function handleDetailsClose() {
		setDetailsOpen(false);
		setSelectedAudit(null);
	}

	return (
		<>
			<AuditCard
				loading={user.isFetching}
				bodyCardPadding="1.5rem 3rem 2rem"
				header={
					<Text type="header" color="dark">
						Auditoria do usuário
					</Text>
				}
			>
				<Row>
					<Col>
						<DataTable
							data={user.audit.data}
							withFilterOption={false}
							total={user.audit.totalItems}
							pages={user.audit.totalPages}
							currentPage={user.audit.page}
							onPageChanged={user.setAuditPage}
							onPerPageChanged={value => user.setAuditPerPage(+value)}
							loading={user.audit.loading}
							columns={columns}
							tableStyles={userAuditTableStyles}
							withPagination
						/>
						{user.audit.data.length === 0 && !user.audit.loading ? (
							<div className="text-center py-4">
								<Text color="dark">
									Nenhuma auditoria encontrada para este usuário.
								</Text>
							</div>
						) : null}
					</Col>
				</Row>
			</AuditCard>
			<Modal
				show={detailsOpen}
				onHide={handleDetailsClose}
				heading={
					<div className="d-flex align-items-center justify-content-between w-100">
						<Text color="dark" type="header">
							Detalhes da auditoria
						</Text>
					</div>
				}
				footer={null}
				body={
					<Row>
						<Col xs={12}>
							<Text color="dark" type="regular" weight="500">
								{selectedAudit?.title || 'Detalhes'}
							</Text>
							<Row className="mt-3">
								<Col md={6} xs={12} className="mb-2">
									<Text type="label" color="dark">
										Tipo
									</Text>
									<Text type="regular" color="gray" as="p" className="mt-2">
										{selectedAudit?.type?.name || 'Não informado'}
									</Text>
								</Col>
								<Col md={6} xs={12} className="mb-2">
									<Text type="label" color="dark">
										Evento
									</Text>
									<Text type="regular" color="gray" as="p" className="mt-2">
										{selectedAudit?.eventType?.name || 'Não informado'}
									</Text>
								</Col>
								<Col md={6} xs={12} className="mb-2">
									<Text type="label" color="dark">
										Alterado por
									</Text>
									<Text type="regular" color="gray" as="p" className="mt-2">
										{selectedAudit?.userChange?.username || 'Sistema'}
									</Text>
								</Col>
								<Col md={6} xs={12} className="mb-2">
									<Text type="label" color="dark">
										Data
									</Text>
									<Text type="regular" color="gray" as="p" className="mt-2">
										{formatDate(selectedAudit?.createdAt)}
									</Text>
								</Col>
							</Row>
							<div className="mt-3">
								{(selectedAudit?.descriptions || '')
									.split('\n')
									.filter(Boolean)
									.map((line, index) => (
										<Text key={`${line}-${index}`} type="regular" color="gray" as="p" className="m-0 mt-2">
											{line}
										</Text>
									))}
								{!selectedAudit?.descriptions ? (
									<Text type="regular" color="gray" as="p" className="mt-2">
										Nenhuma informação detalhada disponível.
									</Text>
								) : null}
							</div>
						</Col>
					</Row>
				}
			/>
		</>
	);
}
